import get from 'lodash/get';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import React from 'react';
import { ForgeList, ForgeIcon, ForgeInlineMessage } from '@tylertech/forge-react';

import { TableActionsDescriptorLinks } from './TableActionsDescriptorLinks';
import { useTableActionsResults } from 'common/visualizations/views/agGridReact/customHooks/useTableActionsResults';
import {
  ApplicationDescriptor,
  hasEmbeddedExecutionMethod,
} from 'common/visualizations/views/agGridReact/helpers/tableActionsHelper';
import type { TableActionsConfigs } from 'common/visualizations/views/agGridReact/TableActionsButton/types';
import { I18n } from 'common/i18n';

const scope = 'shared.visualizations.agTable.actions.errors';

export interface ContentProps {
  contextParams: TableActionsConfigs;
  onLinkClick: (url: string) => void;
  newTableActions: string[];
}

export interface ProcessDescriptor {
  id: string;
  name: string;
}

export interface TableActionsResults {
  errorCode?: number;
  applicationDescriptors?: ApplicationDescriptor[];
  processDescriptors?: ProcessDescriptor[];
}

export const TableActionsContent: React.FC<ContentProps> = ({
  contextParams,
  newTableActions,
  onLinkClick,
}) => {
  const { loading, tableActionsResults }: { loading: boolean; tableActionsResults: TableActionsResults } =
    useTableActionsResults(contextParams);

  const { applicationDescriptors = [] } = get(tableActionsResults, '[0]', {});

  const excludeEmbeddedApplicationDescriptors = applicationDescriptors.filter(
    (action: ApplicationDescriptor) => !hasEmbeddedExecutionMethod(action),
  );

  if (loading) {
    return <div className="spinner-default" data-testid="table-actions-spinner"></div>;
  }

  if (has(tableActionsResults, 'errorCode')) {
    let label;
    let icon;
    switch (tableActionsResults.errorCode) {
      case 401:
        label = 'unauthorized';
        icon = 'info';
        break;
      case 404:
        label = 'not_found';
        icon = 'error';
        break;
      case 400:
        label = 'no_results';
        icon = 'info';
        break;
      default:
        label = 'unexpected';
        icon = 'info';
        break;
    }

    return (
      <ForgeInlineMessage theme="error">
        <ForgeIcon slot="icon" name={icon}></ForgeIcon>
        <p>{I18n.t(label, { scope })}</p>
      </ForgeInlineMessage>
    );
  }

  if (isEmpty(tableActionsResults)) {
    return (
      <ForgeInlineMessage theme="error">
        <ForgeIcon slot="icon" name="info"></ForgeIcon>
        <p data-testid="table-actions-error-message">{I18n.t('no_results', { scope })}</p>
      </ForgeInlineMessage>
    );
  }

  return (
    <div className="links">
      <ForgeList>
        <TableActionsDescriptorLinks
          descriptors={excludeEmbeddedApplicationDescriptors}
          onLinkClick={onLinkClick}
          newTableActions={newTableActions}
        />
      </ForgeList>
    </div>
  );
};
